<script lang="ts">
	import type { Snippet } from 'svelte';
	import { fade, fly } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';
	import Icon from '@iconify/svelte';
	import { btn } from '$lib/actions';

	function portal(node: HTMLElement) {
		document.body.appendChild(node);
		return {
			destroy() {
				node.parentNode?.removeChild(node);
			}
		};
	}

	let {
		title = '',
		open,
		onclose,
		maxWidth = 'max-w-md',
		dismissable = true,
		hideHeader = false,
		bareFooter = false,
		fullScreen = false,
		children,
		subheader = undefined,
		footer = undefined
	}: {
		title?: string;
		open: boolean;
		onclose: () => void;
		maxWidth?: string;
		dismissable?: boolean;
		hideHeader?: boolean;
		bareFooter?: boolean;
		fullScreen?: boolean;
		children: Snippet;
		subheader?: Snippet;
		footer?: Snippet;
	} = $props();

	$effect(() => {
		if (open) {
			const scrollY = window.scrollY;
			document.body.style.position = 'fixed';
			document.body.style.top = `-${scrollY}px`;
			document.body.style.left = '0';
			document.body.style.right = '0';
			document.body.style.width = '100%';
			document.documentElement.classList.add('overflow-hidden');
			document.body.classList.add('overflow-hidden');
			return () => {
				const restoredY = parseInt(document.body.style.top || '0', 10);
				document.body.style.position = '';
				document.body.style.top = '';
				document.body.style.left = '';
				document.body.style.right = '';
				document.body.style.width = '';
				document.documentElement.classList.remove('overflow-hidden');
				document.body.classList.remove('overflow-hidden');
				window.scrollTo(0, -restoredY);
			};
		}
	});

</script>

<svelte:window
	onkeydown={(e) => {
		if (open && dismissable && e.key === 'Escape') onclose();
	}}
/>


{#if open}
	<div use:portal class="fixed inset-0 z-50 flex items-center justify-center {fullScreen ? '' : 'p-4'}">
		{#if dismissable}
			<button type="button" transition:fade={{ duration: fullScreen ? 220 : 0 }} class="absolute inset-0 bg-black/60" onclick={onclose} onwheel={(e) => e.preventDefault()} ontouchmove={(e) => e.preventDefault()} aria-label="Close modal"></button>
		{:else}
			<div transition:fade={{ duration: fullScreen ? 220 : 0 }} class="absolute inset-0 bg-black/60" onwheel={(e) => e.preventDefault()} ontouchmove={(e) => e.preventDefault()} role="presentation"></div>
		{/if}

		<div transition:fly={{ y: fullScreen ? 24 : 0, duration: fullScreen ? 260 : 0, easing: cubicOut }} class="modal-anim pixel-frame relative z-10 flex w-full flex-col overflow-hidden {fullScreen ? 'h-full max-w-none' : maxWidth}" style="animation: {fullScreen ? 'none' : 'modal-in 200ms ease both'}; max-height: {fullScreen ? '100dvh' : 'min(44rem, calc(100dvh - 2rem))'}">
			<div class="pixel-frame absolute inset-0 bg-(--guess-frame-outer)"></div>
			<div class="pixel-frame absolute inset-0.5 bg-(--guess-frame-highlight)"></div>
			<div class="pixel-frame absolute inset-1.5 bg-(--guess-frame-shadow)"></div>
			<div class="pixel-frame absolute inset-2 bg-(--guess-frame-fill)"></div>
			<div class="pixel-frame absolute inset-2.5 bg-(--guess-frame-shadow)"></div>

			<div class="modal-stack relative flex min-h-0 flex-1 flex-col p-2.5">
				<!-- Header -->
				{#if !hideHeader}
				<div class="pixel-frame relative h-12 shrink-0 overflow-hidden">
					<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-2 bg-(--bottom-nav-frame-fill)"></div>
					<div class="pixel-frame absolute inset-2.5 bg-(--bottom-nav-frame-shadow)"></div>
					<div class="modal-header-row relative flex h-full items-center justify-between gap-2 px-1.5 text-(--bottom-nav-frame-text)">
						{#if title}
							<h2 class="font-bold uppercase">{title}</h2>
						{:else}
							<div></div>
						{/if}
						{#if dismissable}
							<button use:btn type="button" onclick={onclose} aria-label="Close" class="pixel-frame relative size-7 shrink-0 cursor-pointer transition-transform active:translate-y-px">
								<div class="pixel-frame absolute inset-0 bg-(--nav-action-frame-outer)"></div>
								<div class="pixel-frame absolute inset-0.5 bg-(--nav-action-frame-highlight)"></div>
								<div class="pixel-frame absolute inset-1 bg-(--nav-action-frame-shadow)"></div>
								<div class="pixel-frame absolute inset-1.5 bg-(--nav-action-frame-fill)"></div>
								<div class="pixel-frame absolute inset-1.5 flex items-center justify-center text-(--nav-action-frame-text)">
									<Icon icon="mdi:close" class="size-4" />
								</div>
							</button>
						{/if}
					</div>
				</div>
				{/if}

				<!-- Subheader -->
				{#if subheader}
					<div class="relative shrink-0 px-1 py-1">
						{@render subheader()}
					</div>
				{/if}

				<!-- Body -->
				<div class="pixel-frame relative flex min-h-0 flex-1 flex-col overflow-hidden" style="flex-basis: auto;">
					<div class="pixel-frame absolute inset-0 bg-(--guess-frame-outer)"></div>
					<div class="pixel-frame absolute inset-0.5 bg-(--guess-frame-highlight)"></div>
					<div class="pixel-frame absolute inset-1.5 bg-(--guess-frame-shadow)"></div>
					<div class="pixel-frame absolute inset-2 bg-(--guess-frame-fill)"></div>
					<div class="pixel-frame absolute inset-2.5 bg-(--guess-frame-shadow)"></div>
					<div class="relative m-3 min-h-0 flex-1 overflow-y-auto overscroll-contain px-2 py-2 text-(--guess-frame-text) {fullScreen ? 'flex flex-col' : ''}" style="contain: paint; isolation: isolate;">
						{#if fullScreen}
							<div class="mx-auto my-auto w-full {maxWidth}">{@render children()}</div>
						{:else}
							{@render children()}
						{/if}
					</div>
				</div>

				<!-- Footer -->
				{#if footer}
					{#if bareFooter}
						<div class="relative mx-auto flex min-h-12 w-full shrink-0 items-center px-3 py-2 text-(--guess-frame-text) {fullScreen ? maxWidth : ''}">
							{@render footer()}
						</div>
					{:else}
						<div class="pixel-frame relative shrink-0">
							<div class="pixel-frame absolute inset-0 bg-(--bottom-nav-frame-outer)"></div>
							<div class="pixel-frame absolute inset-0.5 bg-(--bottom-nav-frame-highlight)"></div>
							<div class="pixel-frame absolute inset-1.5 bg-(--bottom-nav-frame-shadow)"></div>
							<div class="pixel-frame absolute inset-2 bg-(--bottom-nav-frame-fill)"></div>
							<div class="pixel-frame absolute inset-2.5 bg-(--bottom-nav-frame-shadow)"></div>
							<div class="relative flex min-h-12 items-center px-3 py-2 text-(--bottom-nav-frame-text)">
								{@render footer()}
							</div>
						</div>
					{/if}
				{/if}
			</div>
		</div>
	</div>
{/if}
